/**
 * @file     InternalLinksBlock.tsx
 * @purpose  Renders a content-engine internal_links block as a "Keep reading" list with hover affordance.
 */
'use client';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from 'next/link';
import { useAppTheme } from '@/core/theme/useAppTheme';
import type { ContentBlock } from '@/services/content-engine/types';

type InternalLinksBlockProps = Extract<ContentBlock, { t: 'internal_links' }>;

const ChevronIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 6l6 6-6 6" />
  </svg>
);

export const InternalLinksBlock = ({ caption, items }: InternalLinksBlockProps) => {
  const { theme } = useAppTheme();

  if (items.length === 0) return null;

  return (
    <Box component="section">
      {caption ? (
        <Typography
          component="h2"
          sx={{
            fontSize: theme.typography.caption,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontWeight: 600,
            color: theme.semanticColors.textMuted,
            mb: '10px',
          }}
        >
          {caption}
        </Typography>
      ) : null}

      <Box sx={{ borderTop: `1px solid ${theme.semanticColors.borderSubtle}` }}>
        {items.map((item) => (
          <Box
            key={`${item.href}-${item.label}`}
            component={Link}
            href={item.href}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              minHeight: 48,
              py: '12px',
              cursor: 'pointer',
              textDecoration: 'none',
              borderBottom: `1px solid ${theme.semanticColors.borderSubtle}`,
              fontSize: theme.typography.bodySm,
              color: theme.semanticColors.textSecondary,
              transition: 'color 180ms ease, padding-left 180ms ease',
              '&:hover': { color: theme.semanticColors.textPrimary, pl: '8px' },
              '&:hover svg': { color: theme.semanticColors.interactivePrimary },
              '&:focus-visible': {
                outline: `2px solid ${theme.semanticColors.interactiveFocusRing}`,
                outlineOffset: '-2px',
              },
              '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { pl: 0 } },
            }}
          >
            <Box component="span">{item.label}</Box>
            <Box component="span" sx={{ display: 'inline-flex', color: theme.semanticColors.textMuted, flexShrink: 0 }}>
              <ChevronIcon />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
